import React, { useEffect, useRef, useState } from "react";

/**
 * useInView — one-shot IntersectionObserver hook.
 * Returns [ref, isVisible]. Once visible, stays true (no re-fire on scroll up).
 */
export const useInView = (options = {}) => {
  const { threshold = 0.15, rootMargin = "0px 0px -60px 0px" } = options;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
};

/**
 * Reveal — wraps children with a fade-in + translateY reveal on scroll.
 * Respects prefers-reduced-motion.
 *
 * Props:
 *  - delay: ms
 *  - as: element type (default "div")
 *  - className: extra classes
 */
export const Reveal = ({ children, delay = 0, as: Tag = "div", className = "", ...rest }) => {
  const [ref, visible] = useInView();
  const style = {
    transitionDelay: `${delay}ms`,
  };
  return (
    <Tag
      ref={ref}
      style={style}
      className={`reveal ${visible ? "reveal-in" : ""} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
